// 詳細ページの fetch を loading/notfound/error の3状態で扱う共通ヘルパ。
// 従来は fetch 失敗も「存在しません」に丸められていたため、通信断と未存在を区別する。
import { JSX, useEffect, useState } from "react";
import { Alert, Stack } from "@mui/material";
import StyledButton from "./StyledButton";
import { ContentLoading } from "./Loading";
import { ApiError, ErrorCode } from "../api/ApiError";

// ApiError.code は Api.ts の request() で json.code(ErrorCode)がそのままセットされる。
// NotFound 系は ErrorCode.NotFound(4040000)〜InternalServerError 未満の連番で表現される。
function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  return error.code >= ErrorCode.NotFound && error.code < ErrorCode.InternalServerError;
}

type DetailFetchState<T> =
  | { status: "loading" }
  | { status: "error" }
  | { status: "notfound" }
  | { status: "ready"; data: T };

// fetcher は該当データ無しを null で返す前提(取得失敗は reject する)。deps に retry 用のカウンタを含めると再試行できる。
export function useDetailFetch<T>(fetcher: () => Promise<T | null>, deps: unknown[]): DetailFetchState<T> {
  const [state, setState] = useState<DetailFetchState<T>>({ status: "loading" });

  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    fetcher()
      .then((data) => {
        if (!active) {
          return;
        }
        if (data == null) {
          setState({ status: "notfound" });
        } else {
          setState({ status: "ready", data });
        }
      })
      .catch((error) => {
        if (active) {
          setState({ status: isNotFoundError(error) ? "notfound" : "error" });
        }
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

export function useDetailRetry(): [number, () => void] {
  const [count, setCount] = useState(0);
  return [count, () => setCount((c) => c + 1)];
}

// loading/error/notfound を画面表示に変換する。ready のときのみ children を描画する。
export function DetailGuard<T>({ state, notFoundLabel, onRetry, children }: {
  state: DetailFetchState<T>;
  notFoundLabel: string;
  onRetry?: () => void;
  children: (data: T) => JSX.Element;
}): JSX.Element {
  if (state.status === "loading") {
    return <ContentLoading />;
  }
  if (state.status === "error") {
    return (
      <Stack p={4} gap={2} alignItems="flex-start">
        <Alert severity="error">読み込みに失敗しました。再度お試しください。</Alert>
        {onRetry && <StyledButton buttonStyle="primary" onClick={onRetry}>再試行</StyledButton>}
      </Stack>
    );
  }
  if (state.status === "notfound") {
    return <Stack p={4}><Alert severity="error">{notFoundLabel} は存在しません</Alert></Stack>;
  }
  return children(state.data);
}
